import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { showToast } from '../utils/toast';
import { createProductSlug, appendUniqueSuffix, randomSuffix } from '../utils/slug';
import {
  MAX_ADDITIONAL_IMAGES,
  humanFileSize,
  uploadOptimizedImage
} from '../utils/imageUpload';
import logo from '../assets/pokeshani-logo.png';
import './CreateDigitalProduct.css';
interface ProductData {
  thumbnail: string;
  additionalImages: string[];
  title: string;
  description: string;
  price: number;
  Type: string;
  category: string;
  access_url: string;
  payment_type: string; // Add this line
}

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const CreateDigitalProduct: React.FC = () => {
  const [formData, setFormData] = useState<ProductData>({ 
    thumbnail: '',
    additionalImages: [],
    title: '',
    description: '',
    price: 0,
    Type: '',
    category: '',
    access_url: '',
    payment_type: 'one-time' // Add this line
  });

  const [earnings, setEarnings] = useState<number>(0);
  const [showEarnings, setShowEarnings] = useState<boolean>(false);

  const calculateEarnings = (price: number) => {
    return price; // Creators keep 100% of earnings
  };

  useEffect(() => {
    setEarnings(calculateEarnings(formData.price));
    setShowEarnings(formData.payment_type !== 'free' && formData.price > 0);
  }, [formData.price, formData.payment_type]);

  const typeOptions = ['Courses', 'Ebooks', 'Templates', 'Videos'];
  const categoryOptions = ['Business', 'Marketing', 'Programming'];

  const [customType, setCustomType] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([]);
  
  /**
   * Thumbnail upload.
   *
   * The image is resized and re-encoded in the browser first: Supabase Storage
   * egress (the "Cached Egress" quota) is billed per byte downloaded, so a
   * multi-megabyte original would cost quota on every page view for the life of
   * the product. See src/utils/imageUpload.ts.
   */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      try {
        const uploaded = await uploadOptimizedImage(supabase, 'thumbnails', file);

        setFormData(prev => ({
          ...prev,
          thumbnail: uploaded.url
        }));

        if (uploaded.originalBytes > uploaded.bytes) {
          showToast.success(
            `Thumbnail optimised: ${humanFileSize(uploaded.originalBytes)} \u2192 ${humanFileSize(uploaded.bytes)}`
          );
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        showToast.error(
          error instanceof Error ? error.message : 'Failed to upload image. Please try again.'
        );
      }
    }
  };
  
  const handleAdditionalImagesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const remaining = MAX_ADDITIONAL_IMAGES - formData.additionalImages.length;
      if (remaining <= 0) {
        showToast.error(`You can add up to ${MAX_ADDITIONAL_IMAGES} extra images per product.`);
        return;
      }

      const files = Array.from(e.target.files).slice(0, remaining);
      const newImageUrls: string[] = [];
      const newPreviews: string[] = [];

      try {
        // Upload all selected files, each one resized + re-encoded first so the
        // bucket only ever holds web-sized images.
        for (const file of files) {
          const uploaded = await uploadOptimizedImage(supabase, 'additional-images', file);

          newImageUrls.push(uploaded.url);
          newPreviews.push(URL.createObjectURL(uploaded.file));
        }
        
        // Update form data and previews
        setFormData(prev => ({
          ...prev,
          additionalImages: [...prev.additionalImages, ...newImageUrls]
        }));
        
        setAdditionalImagePreviews(prev => [...prev, ...newPreviews]);
      } catch (error) {
        console.error('Error uploading files:', error);
        showToast.error(
          error instanceof Error ? error.message : 'Failed to upload images. Please try again.'
        );
      }
    }
  };
  
  const removeAdditionalImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index)
    }));
    
    setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
  };
  
  const [wordCount, setWordCount] = useState<number>(0);
  const MIN_WORDS = 10;

  // Clean up object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      additionalImagePreviews.forEach(preview => {
        URL.revokeObjectURL(preview);
      });
    };
  }, [additionalImagePreviews]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    
    if (id === 'description') {
      const words = value.trim().split(/\s+/).filter(word => word.length > 0);
      setWordCount(words.length);
    }

    setFormData(prev => ({
      ...prev,
      [id]: id === 'price' ? parseFloat(value) : value
    }));
  };

  const validateForm = () => {
    if (wordCount < MIN_WORDS) {
      showToast.error(`Description must be at least ${MIN_WORDS} words. Current: ${wordCount} words`);
      return false;
    }
    
    // For non-free products, price is required
    if (formData.payment_type !== 'free' && formData.price <= 0) {
      showToast.error('Price must be greater than $0 for paid products');
      return false;
    }
    
    return true;
  };

  /**
   * Build a short, unique, keyword-friendly slug for the product.
   *
   * Long titles used to produce very long URLs (the longest in the catalogue is
   * 142 characters). createProductSlug caps the slug at a word boundary, and a
   * short suffix is only added when that slug is already taken. See
   * src/utils/slug.ts for the reasoning and the alternatives considered.
   */
  const createUniqueSlug = async (title: string): Promise<string> => {
    const base = createProductSlug(title);
    let candidate = base;

    // Two or three attempts is plenty: a collision only happens when two
    // products share the same leading words.
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const { data, error } = await supabase
        .from('digitalproducts')
        .select('id')
        .eq('slug', candidate)
        .limit(1);

      // On a lookup error, keep the candidate and let the insert report the
      // real problem rather than silently changing the slug.
      if (error) return candidate;
      if (!data || data.length === 0) return candidate;

      candidate = appendUniqueSuffix(base, randomSuffix());
    }

    return candidate;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        showToast.error('Please sign in to create a digital product');
        return;
      }

      const slug = await createUniqueSlug(formData.title);
      // For free products, set price to 0
      const price = formData.payment_type === 'free' ? 0 : formData.price;
      
      const { data, error } = await supabase
        .from('digitalproducts')
        .insert({
          thumbnail: formData.thumbnail,
          additional_images: formData.additionalImages,
          title: formData.title,
          description: formData.description,
          price: price,
          Type: formData.Type,
          category: formData.category,
          access_url: formData.access_url,
          slug,
          creator_id: user.id,
          payment_type: formData.payment_type // Add this line
        })
        .select();

      if (error) {
        console.error('Database error:', error);
        showToast.error(error.message || 'Failed to create digital product');
        return;
      }

      if (data) {
        showToast.success('🎉 Sales page created successfully! Redirecting to your new page...');
        // Redirect to the sales page with the slug
        setTimeout(() => {
          window.location.href = `/sales/${slug}`;
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      showToast.error('❌ Failed to create sales page. Please try again.');
    }
  };

  return (
    <div className="digital-product-container">
      <div className="dashboard-header">
        <div className="logo-container">
          <img src={logo} alt="PokeShani Logo" className="header-logo" />
          <span className="site-name">PokeShani</span>
        </div>
        <div className="header-actions">
          <button className="nav-btn help-btn" onClick={() => window.location.href = '/faq'}>
            Help
          </button>
          <button className="nav-btn dashboard-btn" onClick={() => window.location.href = '/dashboard'}>
            Dashboard
          </button>
        </div>
      </div>      

      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="digital-product-card">
            <div className="card-body">
              <h3 className="digital-product-subtitle">Create Your Sales Page</h3>
              <form onSubmit={handleSubmit} className="digital-product-form">
                <div className="mb-3">
                  <label htmlFor="thumbnail" className="form-label">Thumbnail Image</label>
                  <input
                    type="file"
                    className="form-control"
                    id="thumbnail"
                    onChange={handleFileChange}
                    accept="image/*"
                    required
                  />
                </div>
                
                {/* Additional Images Section */}
                <div className="mb-4">
                  <label htmlFor="additionalImages" className="form-label">Add More Images (Optional)</label>
                  <input
                    type="file"
                    className="form-control"
                    id="additionalImages"
                    onChange={handleAdditionalImagesChange}
                    accept="image/*"
                    multiple
                  />
                  {additionalImagePreviews.length > 0 && (
                    <div className="mt-3">
                      <div className="d-flex flex-wrap gap-2">
                        {additionalImagePreviews.map((preview, index) => (
                          <div key={index} className="position-relative" style={{ width: '100px', height: '100px' }}>
                            <img 
                              src={preview} 
                              alt={`Preview ${index + 1}`} 
                              className="img-thumbnail w-100 h-100 object-fit-cover"
                            />
                            <button
                              type="button"
                              className="btn-close position-absolute top-0 end-0"
                              aria-label="Remove"
                              onClick={() => removeAdditionalImage(index)}
                              style={{ 
                                fontSize: '1rem', 
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                borderRadius: '50%',
                                padding: '2px',
                                border: '1px solid #ccc',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            ></button>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 p-2 bg-light rounded">
                        <span className="text-dark fw-bold fs-6">
                          {formData.additionalImages.length} additional image(s) uploaded
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="description" className="form-label">Description (Minimum {MIN_WORDS} words)</label>
                  <textarea
                    className="form-control"
                    id="description"
                    rows={8}
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    placeholder={`Please provide a detailed description of your digital product (minimum ${MIN_WORDS} words)`}
                  />
                  <div className={`word-count ${wordCount < MIN_WORDS ? 'text-danger' : 'text-success'}`} style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                    {wordCount} / {MIN_WORDS} words {wordCount < MIN_WORDS ? `(${MIN_WORDS - wordCount} more words needed)` : '✓'}
                  </div>
                </div>
                {formData.payment_type !== 'free' && (
                  <div className="mb-3">
                    <label htmlFor="price" className="form-label">Price (USD)</label>
                    <div className="price-calculator">
                      <input
                        type="number"
                        className="form-control"
                        id="price"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={handleInputChange}
                        required={formData.payment_type !== 'free'}
                      />
                      <div className={`earnings-display ${showEarnings ? 'visible' : ''}`}>
                        <span>Your earnings per sale: ${earnings.toFixed(2)}</span>
                        <small style={{ color: '#28a745' }}>You keep 100% of your earnings - No platform fees!</small>
                      </div>
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <label htmlFor="Type" className="form-label">Type</label>
                  <input
                    type="text"
                    className="form-control"
                    id="Type"
                    list="typeOptions"
                    value={formData.Type}
                    onChange={(e) => handleInputChange(e)}
                    placeholder="Select or type your own"
                    required
                  />
                  <datalist id="typeOptions">
                    {typeOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </datalist>
                </div>
                <div className="mb-3">
                  <label htmlFor="category" className="form-label">Category</label>
                  <input
                    type="text"
                    className="form-control"
                    id="category"
                    list="categoryOptions"
                    value={formData.category}
                    onChange={(e) => handleInputChange(e)}
                    placeholder="Select or type your own"
                    required
                  />
                  <datalist id="categoryOptions">
                    {categoryOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </datalist>
                </div>
                <div className="mb-3">
                  <label htmlFor="access_url" className="form-label">Access URL</label>
                  <div className="access-url-help">
                    <span className="access-url-description">The link where buyers can download or access this product</span>
                    <a href="/access-url" className="access-url-help-link" target="_blank" rel="noopener noreferrer">
                      Don't know what this is? Get help
                    </a>
                  </div>
                  <input
                    type="url"
                    className="form-control"
                    id="access_url"
                    value={formData.access_url}
                    onChange={handleInputChange}
                    required
                    placeholder="https://example.com/your-product-access-link"
                  />
                </div>
                
                {/* Add Payment Type Selection */}
                <div className="mb-3">
                  <label htmlFor="payment_type" className="form-label">Payment Type</label>
                  <select
                    className="payment-type-select"
                    id="payment_type"
                    value={formData.payment_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="one-time">One-Time Purchase</option>
                    <option value="subscription">Subscription</option>
                    <option value="free">Free Product</option>
                  </select>
                  {formData.payment_type === 'subscription' && (
                    <div className="payment-type-note">
                      <strong>Subscription Note:</strong> Buyers will receive a 30-day access token that can be renewed.
                    </div>
                  )}
                  {formData.payment_type === 'free' && (
                    <div className="payment-type-note">
                      <strong>Free Product Note:</strong> Buyers can access this product without payment. No email confirmation will be sent.
                    </div>
                  )}
                </div>
                
                <button type="submit" className="btn btn-primary create-sales-btn">Create Sales Page</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDigitalProduct;
